import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as Styles from './TemplatesList.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { useStudio } from '@/hooks/studio';
import useLocale from '@/hooks/useLocale';
import {
  Button,
  IconLoading,
  IconSearchFail,
  Skeleton,
} from 'gatorcomponents';
import BannerOfferPro from '../BannerOfferPro';
import ProTag from '../ProTag/ProTag';
import HeaderDesignSection from '../HeaderDesignSection/HeaderDesignSection';
import { studioAnalytics } from '@/analytics/studio';
import { useResize } from '@/hooks/useResize';
import { FORMATS_TO_RENDER_BY_SLUG } from '../../studio.enum';

const TemplatesList = ({
  isProPlan,
  templates,
  templatesLoading,
  handleShowModalGetPro,
  setSeeMoreTemplates,
  noMoreTemplates,
  slugsListFiltered,
  selectedCategories,
  setSelectedCategories,
  canMadeTemplateRequest,
  displayFilterCategories,
}) => {
  const { openStudio } = useStudio();
  const { studio: studioLocale } = useLocale();
  const windowRect = useResize();

  const { width } = windowRect;
  const isMobile = width < 600;

  const iconLoadingRef = useRef(null);
  const [loadedSections, setLoadedSections] = useState(0);

  const handleTemplateClick = useCallback((templateId, isProTemplate) => {
    studioAnalytics.openTemplateClick();

    if ((isProTemplate && isProPlan) || !isProTemplate) {
      openStudio('template', templateId);
    }

    if (isProTemplate && !isProPlan) {
      handleShowModalGetPro && handleShowModalGetPro();
    }
  }, [openStudio, isProPlan, handleShowModalGetPro]);

  const handleShowMoreInExternal = useCallback(() => {
    studioAnalytics.showMoreTemplatesClick();
    openStudio('dashboard');
  }, [openStudio]);

  const loadMoreTemplates = useCallback(() => {
    setSeeMoreTemplates && setSeeMoreTemplates(true);
  }, [setSeeMoreTemplates]);

  const handleLoadMoreTemplates = useCallback(() => {
    if (noMoreTemplates || templatesLoading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreTemplates();
        }
      },
      { threshold: 1.0 },
    );

    if (iconLoadingRef.current) {
      observer.observe(iconLoadingRef.current);
    }

    return () => {
      if (iconLoadingRef.current) {
        observer.unobserve(iconLoadingRef.current);
      }
    };
  }, [noMoreTemplates, templatesLoading, loadMoreTemplates]);

  useEffect(() => {
    handleLoadMoreTemplates();
  }, [handleLoadMoreTemplates]);

  const handleSectionLoad = useCallback((formatSlug) => {
    const allImages = document.querySelectorAll(`[data-format="${formatSlug}"] img`);
    const allLoaded = Array.from(allImages).every(img => img.complete);

    if (allLoaded) {
      setLoadedSections(prev => prev + 1);
    }
  }, []);

  const renderLoadingTemplates = useCallback((quantity, height, width, shape) => (
    <Styles.FormatListSkeleton>
      <Styles.FormatListHeader>
        <Styles.FormatTitle>
          <Skeleton height="32px" width="483px" variant="rounded" />
        </Styles.FormatTitle>
      </Styles.FormatListHeader>
      <Styles.TemplateCardsSkeleton>
        {Array.from({ length: quantity }).map(() => (
          <Skeleton
            height={height}
            width={width}
            variant={shape}
            key={Math.random().toString(36).substr(2, 9)}
          />
        ))}
      </Styles.TemplateCardsSkeleton>
    </Styles.FormatListSkeleton>
  ), []);

  const renderTemplates = useCallback(({ start, end }) => slugsListFiltered.map((formatSlug, index) => {
    if (index >= start && index <= end && templates[formatSlug] && !!templates[formatSlug].templates.length) {
      if (index > loadedSections) {
        return renderLoadingTemplates(7, '200px', '200px', 'rectangle');
      }

      return (
        <Styles.FormatList key={formatSlug}>
          <Styles.FormatListHeader>
            <Styles.FormatTitle>
              {studioLocale.templateList.formats[formatSlug] || formatSlug}
            </Styles.FormatTitle>
            <Button
              variant="text"
              onClick={() => openStudio('format', templates[formatSlug].format_id)}
              label={studioLocale.templateList.seeMore}
              size="large"
              version="v2"
            />
          </Styles.FormatListHeader>
          <Styles.TemplateCards>
            {templates[formatSlug] && templates[formatSlug].templates.map(template => (
              <Styles.TemplateCard
                key={template.id}
                onClick={() => handleTemplateClick(template.id, template.pro)}
                format={formatSlug}
                data-format={formatSlug}
              >
                {!isProPlan && template.pro && <ProTag position={{ top: '14px', left: '14px' }} size="small" />}
                <Styles.TemplateImage
                  src={template.thumb}
                  alt={studioLocale.templateList.altText}
                  onLoad={() => handleSectionLoad(formatSlug)}
                />
              </Styles.TemplateCard>
            ))}
          </Styles.TemplateCards>
        </Styles.FormatList>
      );
    }
    return null;
  }), [templates, studioLocale.templateList, slugsListFiltered, openStudio, isProPlan, handleTemplateClick, loadedSections, handleSectionLoad, renderLoadingTemplates]);

  const renderEmptyState = () => (
    <Styles.EmptyState>
      <IconSearchFail size="xl" secondaryColor="attentionPure" />
      {studioLocale.templateList.emptyState}
    </Styles.EmptyState>
  );

  if ((templatesLoading || !canMadeTemplateRequest) && !Object.keys(templates).length) {
    return (
      <Styles.Wrapper data-testid="top-banner-wrapper" isProPlan={isProPlan}>
        <HeaderDesignSection
          isProPlan={isProPlan}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          templatesLoading={templatesLoading}
          displayFilterCategories={displayFilterCategories}
        />

        <Styles.SkeletonContent>
          {renderLoadingTemplates(10, '200px', '200px', 'rectangle')}
          {renderLoadingTemplates(10, '314px', '200px', 'rectangle')}
          <Skeleton width="100%" height="400px" variant="rectangle" />
          {renderLoadingTemplates(8, '200px', '308px', 'rectangle')}
          {renderLoadingTemplates(10, '200px', '200px', 'rectangle')}
        </Styles.SkeletonContent>
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper
      data-testid="top-banner-wrapper"
      isProPlan={isProPlan}
    >
      <HeaderDesignSection
        isProPlan={isProPlan}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        templatesLoading={templatesLoading}
        displayFilterCategories={displayFilterCategories}
      />

      {!Object.keys(templates).length && renderEmptyState()}

      {!!Object.keys(templates).length && (
        <>
          {renderTemplates({ start: 0, end: 1 })}

          {!isProPlan && (
            <BannerOfferPro
              handleShowModalGetPro={handleShowModalGetPro}
            />
          )}

          {renderTemplates({ start: 2, end: FORMATS_TO_RENDER_BY_SLUG.length - 1 })}
        </>
      )}

      {noMoreTemplates && !templatesLoading ? (
        <Styles.ButtonWrapper>
          <Button
            label={studioLocale.templateList.showMoreTemplates}
            onClick={handleShowMoreInExternal}
            version="v2"
            size="large"
            width={isMobile ? 'full' : 'fit'}
          />
        </Styles.ButtonWrapper>
      ) : (
        <Styles.InfinityScrollWrapper ref={iconLoadingRef}>
          <IconLoading size="lg" />
        </Styles.InfinityScrollWrapper>
      )}
    </Styles.Wrapper>
  );
};
export default withErrorBoundary(TemplatesList, 'TemplatesList');
